/**
 * Per-post row for the Ads Assistant indexed list — extracted from
 * `AdsAssistantAggregationPostsList.tsx` so each grows independently.
 */
import {
  AdsAssistantAggregationPostActions,
  type AdsPostActionsGenerateOpen,
} from "@/components/ads-assistant/AdsAssistantAggregationPostActions";
import { AdsAssistantPostMediaStrip } from "@/components/ads-assistant/AdsAssistantPostMediaStrip";
import { AdsPostTagChipsRow } from "@/components/ads-assistant/AdsPostTagChipsRow";
import type {
  AdsAssistantPostMediaItem,
  AdsAssistantSocialPostRow,
  AdsAssistantTemplateRow,
} from "@/lib/adsAssistantClient";
import { resolveAdsPostExternalLink } from "@/lib/adsAssistantPostExternalLink";
import { resolveAdsVideoPosterUrl } from "@/lib/adsAssistantVideoPosterResolve";
import { analysisHints, excerpt, formatPublished } from "@/components/ads-assistant/adsAssistantAggregationUtils";

type Props = {
  post: AdsAssistantSocialPostRow;
  templates: AdsAssistantTemplateRow[];
  locationNameById?: Record<string, string>;
  expanded: boolean;
  onToggleBodyExpand: (postId: string) => void;
  onOpenLightbox: (items: AdsAssistantPostMediaItem[], initialIndex: number) => void;
  busyId: string | null;
  busyGreenId: string | null;
  setBusyId: (id: string | null) => void;
  setBusyGreenId: (id: string | null) => void;
  setKbPost: (p: AdsAssistantSocialPostRow | null) => void;
  scopedOrgId?: string | null;
  canQuality?: boolean;
  canIngestPosts?: boolean;
  canReassignAttribution?: boolean;
  onReassignAttribution?: (post: AdsAssistantSocialPostRow) => void;
  onCelebration?: (message: string) => void;
  onReload?: () => void;
  onRequestGenerate?: (p: AdsPostActionsGenerateOpen) => void;
};

export function AdsAssistantAggregationPostRow({
  post: p,
  templates,
  locationNameById,
  expanded,
  onToggleBodyExpand,
  onOpenLightbox,
  busyId,
  busyGreenId,
  setBusyId,
  setBusyGreenId,
  setKbPost,
  scopedOrgId,
  canQuality,
  canIngestPosts,
  canReassignAttribution,
  onReassignAttribution,
  onCelebration,
  onReload,
  onRequestGenerate,
}: Props) {
  const hints = analysisHints(p);
  const templateName =
    p.template_id && templates.length
      ? templates.find((t) => t.id === p.template_id)?.name?.trim() || null
      : null;
  const rp =
    p.raw_payload && typeof p.raw_payload === "object" && !Array.isArray(p.raw_payload)
      ? (p.raw_payload as Record<string, unknown>)
      : {};
  const creative = typeof rp.creative_description === "string" ? rp.creative_description.trim() : "";
  const qr = typeof rp.quality_rating === "string" ? rp.quality_rating : "";
  const qnote = typeof rp.quality_note === "string" ? excerpt(rp.quality_note, 80) : "";
  const platformLabel = String(p.platform || "unknown").replace(/_/g, " ");

  return (
    <li className="rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm dark:border-gray-600 dark:bg-zinc-900/50">
      <div className="flex flex-wrap items-start justify-between gap-2 gap-y-1">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          <span
            className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700 dark:bg-zinc-800 dark:text-gray-200"
            title="Platform"
          >
            {platformLabel}
          </span>
          {p.location_id ? (
            <span
              className="max-w-[10rem] truncate text-[10px] font-medium text-gray-600 dark:text-gray-300"
              title={locationNameById?.[p.location_id] ?? p.location_id}
            >
              {locationNameById?.[p.location_id]?.trim() || "Unknown location"}
            </span>
          ) : null}
          {templateName ? (
            <span
              className="inline-flex max-w-full items-center truncate rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-900 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-100"
              title="Recognized format from your org library template"
            >
              {templateName}
            </span>
          ) : null}
          {typeof p.sale_units_total === "number" && p.sale_units_total > 0 ? (
            <span
              className="inline-flex rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-blue-950 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100"
              title="Total attributed sale units from all teammates on this ad"
            >
              {p.sale_units_total} sale{p.sale_units_total === 1 ? "" : "s"}
            </span>
          ) : null}
          {p.green_portable ? (
            <span
              className="inline-flex rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/45 dark:text-emerald-100"
              title="Marked portable “green” creative (trainer+)"
            >
              Green (portable)
            </span>
          ) : null}
          {p.promoted_exemplar ? (
            <span className="inline-flex rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-950 dark:bg-amber-950/50 dark:text-amber-100">
              Gold exemplar
            </span>
          ) : null}
        </div>
        <time className="shrink-0 text-[11px] font-medium text-gray-500 dark:text-gray-400" dateTime={p.published_at}>
          {formatPublished(p.published_at)}
        </time>
      </div>
      <AdsAssistantPostMediaStrip
        post_media={p.post_media}
        onOpenLightbox={onOpenLightbox}
        onImageError={() => onReload?.()}
        mediaType={p.media_type}
        postThumbnailUrl={resolveAdsVideoPosterUrl(p)}
        externalLink={resolveAdsPostExternalLink(p)}
        ingestControls={
          canIngestPosts
            ? {
                postId: p.id,
                disabled: busyId === p.id || busyGreenId === p.id,
                onAfterChange: () => onReload?.(),
              }
            : undefined
        }
      />
      <div className="mt-2 space-y-1">
        <p
          className={
            "text-sm font-medium leading-snug text-gray-900 dark:text-gray-100 " +
            (expanded ? "whitespace-pre-wrap break-words" : "")
          }
        >
          {expanded ? p.text_content || "(no text)" : excerpt(p.text_content || "(no text)", 200)}
        </p>
        {(p.text_content || "").length > 200 ? (
          <button
            type="button"
            className="text-[11px] font-medium text-gray-600 hover:underline dark:text-gray-400"
            onClick={() => onToggleBodyExpand(p.id)}
          >
            {expanded ? "Show less" : "Show full text"}
          </button>
        ) : null}
      </div>
      {p.categories?.length ? (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {p.categories.map((c) => (
            <span
              key={c.id}
              className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-zinc-800 dark:text-gray-300"
            >
              {c.label}
            </span>
          ))}
        </div>
      ) : null}
      {p.tags?.length ? <AdsPostTagChipsRow tags={p.tags} /> : null}
      {hints.tags.length ? (
        <div className="mt-1.5 flex flex-wrap gap-1" aria-label="Stub analysis tags">
          {hints.tags.map((t) => (
            <span
              key={t}
              className="rounded border border-sky-200/90 bg-sky-50/90 px-1.5 py-0.5 text-[10px] font-medium text-sky-950 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100"
              title="Heuristic tags from the stub analyzer (not LLM-classified)"
            >
              {t.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      ) : null}
      {qr === "good" || qr === "bad" ? (
        <span
          className={
            "mt-1.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium " +
            (qr === "good" ? "bg-emerald-100 text-emerald-900" : "bg-rose-100 text-rose-900")
          }
        >
          {qr === "good" ? "Good example" : "Bad example"}
          {qnote ? ` — ${qnote}` : ""}
        </span>
      ) : null}
      {creative ? (
        <p className="mt-1.5 text-[11px] text-gray-600 dark:text-gray-400">
          <span className="font-medium text-gray-700 dark:text-gray-300">Creative:</span> {excerpt(creative, 120)}
        </p>
      ) : null}
      {hints.summary ? (
        <p className="mt-2 rounded border border-stone-200/90 bg-stone-100/90 px-2 py-1.5 text-[11px] leading-relaxed text-gray-800 dark:border-zinc-600 dark:bg-zinc-800/80 dark:text-gray-200">
          {hints.summary}
        </p>
      ) : null}
      {hints.flags.length ? (
        <ul className="mt-1.5 flex flex-wrap gap-1 list-none" aria-label="Compliance hints">
          {hints.flags.map((f) => (
            <li
              key={f}
              className="rounded bg-amber-100/80 px-1.5 py-0.5 text-[10px] text-amber-950 dark:bg-amber-950/40 dark:text-amber-100"
            >
              {f}
            </li>
          ))}
        </ul>
      ) : null}
      <AdsAssistantAggregationPostActions
        post={p}
        templateName={templateName}
        scopedOrgId={scopedOrgId}
        canIngestPosts={canIngestPosts}
        canReassignAttribution={canReassignAttribution}
        onReassignAttribution={onReassignAttribution}
        canQuality={canQuality}
        canSetTemplate={canQuality}
        templates={templates}
        busyId={busyId}
        busyGreenId={busyGreenId}
        setBusyId={setBusyId}
        setBusyGreenId={setBusyGreenId}
        setKbPost={setKbPost}
        onCelebration={onCelebration}
        onReload={onReload}
        onRequestGenerate={onRequestGenerate}
      />
    </li>
  );
}
